import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReferenceData, useStatus } from '../components/tab-layout';
import { deleteRoute, fetchRoutes, upsertRoute, previewImport, commitImport } from '../lib/api-client';
import { SqlActionModal } from '../components/sql-action-modal';
import { RoutePreviewModal } from '../components/route-preview-modal';
import type { RouteRecord, RouteUpsertRequest, ImportPreviewResponse, ImportCommitResponse } from '../types';
import type { FeatureCollection } from 'geojson';

/** Parse WKT LINESTRING to GeoJSON FeatureCollection */
function wktToGeoJson(wkt: string, name: string) {
  const match = wkt.match(/LINESTRING\((.+)\)/i);
  if (!match) return { type: 'FeatureCollection', features: [] };
  const coords = match[1].split(',').map((pair) => {
    const [lon, lat] = pair.trim().split(/\s+/).map(Number);
    return [lon, lat];
  });
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { name, source: 'OSRM generated by etigo_tool', points: coords.length },
      geometry: { type: 'LineString', coordinates: coords },
    }],
  };
}

function countWktPoints(wkt: string): number {
  const match = wkt.match(/LINESTRING\((.+)\)/i);
  if (!match) return 0;
  return match[1].split(',').length;
}

/** Geocode a place name using Nominatim API (Philippines) */
async function geocodePlace(name: string): Promise<{ lat: number; lon: number } | null> {
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(name)}&format=json&limit=1&countrycodes=ph`;
    const res = await fetch(url, { headers: { 'User-Agent': 'etigo-tool/1.0' } });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json.length) return null;
    return { lat: parseFloat(json[0].lat), lon: parseFloat(json[0].lon) };
  } catch {
    return null;
  }
}

const EMPTY_FORM: RouteUpsertRequest = {
  operator_id: '',
  location_id: '',
  origin_terminal_id: '',
  destination_terminal_id: '',
  origin_name: '',
  destination_name: '',
  route_name: '',
  route_type: 'carousel-bus',
  schedule_raw: '6:00 AM\n6:00 PM',
  fare_raw: 'PHP 200',
  regular_fare: 200,
  vehicle_id: '',
  trip_start_date: new Date().toISOString().slice(0, 10),
  horizon_days: 7,
  available_seats_count: 60,
};

/* Reusable styled components */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{children}</h4>;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs font-medium text-gray-600 mb-1">{children}</label>;
}

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors';
const selectCls = `${inputCls} bg-white`;

export function RoutesTab() {
  const navigate = useNavigate();
  const { operators, terminals, locations, loading: refLoading, error: refError } = useReferenceData();
  const { setMessage } = useStatus();
  const [records, setRecords] = useState<RouteRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<RouteUpsertRequest>(EMPTY_FORM);
  const [extraStopsText, setExtraStopsText] = useState('[]');
  const [subTab, setSubTab] = useState<'single' | 'batch' | 'generated'>('single');
  // Geocoding state for single entry
  const [originCoords, setOriginCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [destCoords, setDestCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [geocoding, setGeocoding] = useState<'origin' | 'dest' | null>(null);
  const [singleRouteWkt, setSingleRouteWkt] = useState<string | null>(null);
  // Batch import state
  const [batchRouteFile, setBatchRouteFile] = useState<File | null>(null);
  const [batchMappingFile, setBatchMappingFile] = useState<File | null>(null);
  const [batchCacheFile, setBatchCacheFile] = useState<File | null>(null);
  const [batchOperatorId, setBatchOperatorId] = useState('');
  const [batchPreview, setBatchPreview] = useState<ImportPreviewResponse | null>(null);
  const [renamedRoutes, setRenamedRoutes] = useState<Record<number, string>>({}); // route index -> new name
  const [editedSchedules, setEditedSchedules] = useState<Record<number, string>>({}); // route index -> new schedule
  const [editedFares, setEditedFares] = useState<Record<number, string>>({}); // route index -> new fare
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResult, setBatchResult] = useState<ImportCommitResponse | null>(null);
  const [pendingSql, setPendingSql] = useState<{ sql: string; title: string } | null>(null);
  const [previewGeoJson, setPreviewGeoJson] = useState<FeatureCollection | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>('');

  const loadRoutes = async () => {
    setLoading(true);
    try {
      const res = await fetchRoutes();
      setRecords(res.routes || []);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to load routes' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoutes();
  }, []);

  const origin = useMemo(() => terminals.find((x) => x.id === form.origin_terminal_id), [terminals, form.origin_terminal_id]);
  const destination = useMemo(() => terminals.find((x) => x.id === form.destination_terminal_id), [terminals, form.destination_terminal_id]);

  const saveRoute = async () => {
    if (!form.operator_id || !form.origin_name || !form.destination_name) {
      setMessage({ type: 'error', text: 'Operator, origin and destination names are required' });
      return;
    }
    try {
      let stops: RouteUpsertRequest['stops'] = undefined;
      if (extraStopsText.trim()) {
        const parsed = JSON.parse(extraStopsText);
        if (!Array.isArray(parsed)) throw new Error('Stops must be a JSON array');
        stops = parsed;
      }
      const payload: RouteUpsertRequest = {
        ...form,
        location_id: form.location_id || undefined,
        origin_terminal_id: form.origin_terminal_id || undefined,
        destination_terminal_id: form.destination_terminal_id || undefined,
        route_name: form.route_name || `${form.origin_name} - ${form.destination_name}`,
        vehicle_id: form.vehicle_id || undefined,
        origin_lat: originCoords?.lat,
        origin_lon: originCoords?.lon,
        destination_lat: destCoords?.lat,
        destination_lon: destCoords?.lon,
        stops,
      };
      const res = await upsertRoute(payload);
      setPendingSql({ sql: res.sql, title: `Route: ${payload.route_name || `${payload.origin_name} - ${payload.destination_name}`}` });
      setForm(EMPTY_FORM);
      setExtraStopsText('[]');
      await loadRoutes();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Save failed' });
    }
  };

  const removeRoute = async (id: string) => {
    if (!confirm('Delete this route SQL reference?')) return;
    try {
      const res = await deleteRoute(id);
      await loadRoutes();
      if (res.sql) setPendingSql({ sql: res.sql, title: 'Delete Route' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Delete failed' });
    }
  };

  const handleBatchPreview = async () => {
    if (!batchRouteFile) { setMessage({ type: 'error', text: 'Routes workbook required' }); return; }
    if (!batchOperatorId && !batchMappingFile) { setMessage({ type: 'error', text: 'Select an operator or provide a mapping file' }); return; }
    setBatchLoading(true); setBatchResult(null);
    try {
      const fd = new FormData();
      fd.append('routesWorkbook', batchRouteFile);
      if (batchMappingFile) fd.append('mappingFile', batchMappingFile);
      if (batchCacheFile) fd.append('routesCacheFile', batchCacheFile);
      if (batchOperatorId) fd.append('operatorId', batchOperatorId);
      fd.append('idempotencyKey', crypto.randomUUID());
      const res = await previewImport(fd);
      setBatchPreview(res);
      setMessage({ type: 'success', text: 'Preview loaded' });
    } catch (err: any) { setMessage({ type: 'error', text: err.message }); }
    finally { setBatchLoading(false); }
  };

  const handleBatchCommit = async () => {
    if (!batchPreview) return;
    setBatchLoading(true);
    try {
      const res = await commitImport({ importId: batchPreview.importId, mode: 'export_sql', conflictStrategy: 'upsert' });
      setBatchResult(res);
      if (res.sql) {
        setPendingSql({ sql: res.sql, title: 'Batch Import SQL' });
      } else {
        setMessage({ type: 'success', text: 'SQL exported' });
      }
    } catch (err: any) { setMessage({ type: 'error', text: err.message }); }
    finally { setBatchLoading(false); }
  };

  if (refLoading) return <div className="text-gray-500 p-8 text-center">Loading reference data...</div>;
  if (refError) return <div className="text-red-500 p-8 text-center">Error: {refError}</div>;

  const subTabCls = (tab: string) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
      subTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
    }`;

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button onClick={() => setSubTab('single')} className={subTabCls('single')}>Single Entry</button>
          <button onClick={() => setSubTab('batch')} className={subTabCls('batch')}>Batch Import</button>
          <button onClick={() => setSubTab('generated')} className={subTabCls('generated')}>
            Generated {records.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">{records.length}</span>}
          </button>
        </div>
      </div>

      {/* === SINGLE ENTRY === */}
      {subTab === 'single' && <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">Route + Trips Seeder</h3>
          <p className="text-xs text-gray-500 mt-0.5">Generate SQL for terminal, route, sub-route, fare, trip, and waypoint records</p>
        </div>

        <div className="p-5 space-y-5">
          {/* --- Section 1: General --- */}
          <div>
            <SectionTitle>General</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <FieldLabel>Operator *</FieldLabel>
                <select value={form.operator_id} onChange={(e) => setForm({ ...form, operator_id: e.target.value })} className={selectCls}>
                  <option value="">Select operator</option>
                  {operators.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel>Location</FieldLabel>
                <select value={form.location_id || ''} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className={selectCls}>
                  <option value="">— None —</option>
                  {locations.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel>Route Type</FieldLabel>
                <select value={form.route_type} onChange={(e) => setForm({ ...form, route_type: e.target.value as 'p2p-bus' | 'carousel-bus' })} className={selectCls}>
                  <option value="carousel-bus">Carousel Bus</option>
                  <option value="p2p-bus">P2P Bus</option>
                </select>
              </div>
            </div>
          </div>

          {/* --- Section 2: Origin & Destination --- */}
          <div>
            <SectionTitle>Origin & Destination</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Origin */}
              <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100 space-y-2">
                <span className="text-xs font-semibold text-blue-700">Origin Terminal *</span>
                <select value={form.origin_terminal_id || ''} onChange={(e) => {
                  const t = terminals.find((x) => x.id === e.target.value);
                  const oName = t?.name || '';
                  const dName = form.destination_name;
                  setForm({ ...form, origin_terminal_id: e.target.value, origin_name: oName, route_name: oName && dName ? `${oName} - ${dName}` : '' });
                  // Set coords from terminal if available
                  if (t?.lat && t?.lon) setOriginCoords({ lat: t.lat, lon: t.lon });
                  else setOriginCoords(null);
                }} className={selectCls}>
                  <option value="">— Select —</option>
                  {terminals.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
                {originCoords && (
                  <div className="text-xs text-blue-600 flex items-center gap-1">
                    📍 {originCoords.lat.toFixed(4)}, {originCoords.lon.toFixed(4)}
                  </div>
                )}
              </div>

              {/* Destination */}
              <div className="bg-green-50/50 rounded-lg p-3 border border-green-100 space-y-2">
                <span className="text-xs font-semibold text-green-700">Destination Terminal *</span>
                <select value={form.destination_terminal_id || ''} onChange={(e) => {
                  const t = terminals.find((x) => x.id === e.target.value);
                  const dName = t?.name || '';
                  const oName = form.origin_name;
                  setForm({ ...form, destination_terminal_id: e.target.value, destination_name: dName, route_name: oName && dName ? `${oName} - ${dName}` : '' });
                  // Set coords from terminal if available
                  if (t?.lat && t?.lon) setDestCoords({ lat: t.lat, lon: t.lon });
                  else setDestCoords(null);
                }} className={selectCls}>
                  <option value="">— Select —</option>
                  {terminals.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
                {destCoords && (
                  <div className="text-xs text-green-600 mt-1">
                    📍 {destCoords.lat.toFixed(4)}, {destCoords.lon.toFixed(4)}
                  </div>
                )}
              </div>
            </div>

            {/* Route name override */}
            <div className="mt-3">
              <FieldLabel>Route Name</FieldLabel>
              <input value={form.route_name || ''} onChange={(e) => setForm({ ...form, route_name: e.target.value })} placeholder="Auto: Origin - Destination" className={inputCls} />
            </div>

            {/* Locate & Preview */}
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={async () => {
                  if (!form.origin_name || !form.destination_name) return;
                  setGeocoding('origin');
                  const [o, d] = await Promise.all([
                    geocodePlace(form.origin_name),
                    geocodePlace(form.destination_name),
                  ]);
                  setGeocoding(null);
                  if (o && d) {
                    setOriginCoords(o);
                    setDestCoords(d);
                    // Fetch OSRM route
                    const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${o.lon},${o.lat};${d.lon},${d.lat}?overview=full&geometries=geojson`;
                    const res = await fetch(osrmUrl);
                    const json = await res.json();
                    if (json.code === 'Ok' && json.routes?.[0]) {
                      const coords = json.routes[0].geometry.coordinates;
                      const points = coords.map(([lon, lat]: [number, number]) => `${lon} ${lat}`).join(', ');
                      setSingleRouteWkt(`LINESTRING(${points})`);
                    } else {
                      setSingleRouteWkt(`LINESTRING(${o.lon} ${o.lat}, ${d.lon} ${d.lat})`);
                    }
                    setMessage({ type: 'success', text: 'Route located' });
                  } else {
                    setMessage({ type: 'error', text: 'Could not geocode one or both terminals' });
                  }
                }}
                disabled={!form.origin_name || !form.destination_name || geocoding !== null}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 cursor-pointer"
              >
                {geocoding ? 'Locating...' : '📍 Locate Route'}
              </button>
              {originCoords && destCoords && (
                <button
                  onClick={() => {
                    if (!singleRouteWkt) return;
                    const geojson = wktToGeoJson(singleRouteWkt, form.route_name || `${form.origin_name} - ${form.destination_name}`);
                    setPreviewGeoJson(geojson as FeatureCollection);
                    setPreviewTitle(form.route_name || `${form.origin_name} - ${form.destination_name}`);
                  }}
                  className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md cursor-pointer"
                >
                  🗺️ Preview Map
                </button>
              )}
              {originCoords && destCoords && (
                <div className="text-xs text-gray-500">
                  {originCoords.lat.toFixed(4)}, {originCoords.lon.toFixed(4)} → {destCoords.lat.toFixed(4)}, {destCoords.lon.toFixed(4)}
                </div>
              )}
            </div>
          </div>

          {/* --- Section 3: Schedule & Fare --- */}
          <div>
            <SectionTitle>Schedule & Fare</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel>Schedule</FieldLabel>
                <textarea value={form.schedule_raw} onChange={(e) => setForm({ ...form, schedule_raw: e.target.value })} className={`${inputCls} h-20 font-mono`} />
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>Fare (raw text)</FieldLabel>
                    <input value={form.fare_raw} onChange={(e) => setForm({ ...form, fare_raw: e.target.value })} placeholder="PHP 200" className={inputCls} />
                  </div>
                  <div>
                    <FieldLabel>Regular Fare (number)</FieldLabel>
                    <input type="number" value={String(form.regular_fare ?? '')} onChange={(e) => setForm({ ...form, regular_fare: e.target.value ? Number(e.target.value) : undefined })} placeholder="200" className={inputCls} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- Section 4: Trip Config --- */}
          <div>
            <SectionTitle>Trip Configuration</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <FieldLabel>Start Date</FieldLabel>
                <input type="date" value={form.trip_start_date || ''} onChange={(e) => setForm({ ...form, trip_start_date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <FieldLabel>Horizon Days</FieldLabel>
                <input type="number" min={1} max={30} value={String(form.horizon_days ?? 7)} onChange={(e) => setForm({ ...form, horizon_days: e.target.value ? Number(e.target.value) : 7 })} className={inputCls} />
              </div>
              <div>
                <FieldLabel>Available Seats</FieldLabel>
                <input type="number" min={1} max={100} value={String(form.available_seats_count ?? 60)} onChange={(e) => setForm({ ...form, available_seats_count: e.target.value ? Number(e.target.value) : 60 })} className={inputCls} />
              </div>
              <div>
                <FieldLabel>Vehicle ID</FieldLabel>
                <input value={form.vehicle_id || ''} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} placeholder="Auto if empty" className={inputCls} />
              </div>
            </div>
          </div>

          {/* --- Section 5: Intermediate Stops --- */}
          <div>
            <SectionTitle>Intermediate Stops (Optional)</SectionTitle>
            <p className="text-[11px] text-gray-400 mb-1">JSON array of stops between origin and destination. Each: {`{"name": "Stop", "lat": 14.5, "lon": 121.0}`}</p>
            <textarea value={extraStopsText} onChange={(e) => setExtraStopsText(e.target.value)} className={`${inputCls} h-20 font-mono text-xs`} />
          </div>

          {/* --- Actions --- */}
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <button onClick={saveRoute} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors">
              Save Route + Generate Trips
            </button>
            <button onClick={() => { setForm(EMPTY_FORM); setExtraStopsText('[]'); }} className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors">
              Reset
            </button>
            {origin && destination && (
              <span className="text-xs text-gray-500 ml-auto">
                Selected: <strong>{origin.name}</strong> → <strong>{destination.name}</strong>
              </span>
            )}
          </div>
        </div>
      </div>}

      {/* === GENERATED ROUTES === */}
      {subTab === 'generated' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-base font-semibold text-gray-800">Generated Routes</h3>
          {loading && <span className="text-xs text-gray-400 animate-pulse">Loading...</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Route</th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Fare</th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Trips</th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {records.length === 0 && (
                <tr><td className="px-4 py-6 text-gray-400 text-center" colSpan={6}>No routes generated yet.</td></tr>
              )}
              {records.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-2.5 font-medium text-gray-800">{row.route_name || `${row.origin_name} - ${row.destination_name}`}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      row.route_type === 'p2p-bus' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {row.route_type}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 whitespace-pre-line text-xs">{row.schedule_raw}</td>
                  <td className="px-4 py-2.5 text-gray-600">{row.fare_raw}</td>
                  <td className="px-4 py-2.5 text-center font-medium">{row.trip_count ?? 0}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => removeRoute(row.id)} className="text-red-500 hover:text-red-700 text-xs font-medium transition-colors">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>}

      {/* === BATCH IMPORT === */}
      {subTab === 'batch' && <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">Batch Import (XLSX)</h3>
          <p className="text-xs text-gray-500 mt-0.5">Upload routes workbook and mapping file to import in bulk</p>
        </div>
        <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1" title="Select the bus operator company for this import">Operator {batchMappingFile ? '(from mapping)' : '*'}</label>
                <select value={batchOperatorId} onChange={(e) => setBatchOperatorId(e.target.value)} className={selectCls}>
                  <option value="">Select operator</option>
                  {operators.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1" title="XLSX file with route templates (origin, destination, schedule, fare)">Routes Workbook *</label>
                <input type="file" accept=".xlsx,.xls" onChange={(e) => setBatchRouteFile(e.target.files?.[0] || null)} className="text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1" title="JSON file mapping sheet names to operator_id and terminal_id">Mapping File (optional)</label>
                <input type="file" accept=".xlsx,.xls,.csv,.json" onChange={(e) => setBatchMappingFile(e.target.files?.[0] || null)} className="text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1" title="Pre-computed OSRM routes cache (run: pnpm run geocode-routes)">Routes Cache (optional)</label>
                <input type="file" accept=".json" onChange={(e) => setBatchCacheFile(e.target.files?.[0] || null)} className="text-sm" />
              </div>
            </div>
            <button onClick={handleBatchPreview} disabled={batchLoading || !batchRouteFile || (!batchOperatorId && !batchMappingFile)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors cursor-pointer disabled:opacity-50">
              {batchLoading ? 'Processing...' : 'Preview Import'}
            </button>
            {batchPreview && (
              <div className="space-y-4">
                {/* Summary bar */}
                <div className="bg-blue-50 rounded-md p-3 border border-blue-200 flex items-center gap-6 text-sm">
                  <div>
                    <span className="text-xs text-blue-500 uppercase tracking-wider block">Routes</span>
                    <span className="text-lg font-semibold text-blue-800">{batchPreview.counts.route_templates}</span>
                  </div>
                  {batchPreview.warnings.length > 0 && (
                    <div>
                      <span className="text-xs text-amber-500 uppercase tracking-wider block">Warnings</span>
                      <span className="text-lg font-semibold text-amber-600">{batchPreview.warnings.length}</span>
                    </div>
                  )}
                </div>

                {/* Route templates table */}
                {batchPreview.sample.route_templates.length > 0 && (
                  <div className="border border-gray-200 rounded-md overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fare</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sheet</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Path</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {batchPreview.sample.route_templates.map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50/50">
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={renamedRoutes[i] ?? r.route_name_normalized}
                                onChange={(e) => setRenamedRoutes(prev => ({ ...prev, [i]: e.target.value }))}
                                placeholder={r.route_name_normalized}
                                className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <textarea
                                value={editedSchedules[i] ?? r.schedule_raw ?? ''}
                                onChange={(e) => setEditedSchedules(prev => ({ ...prev, [i]: e.target.value }))}
                                placeholder={r.schedule_raw}
                                rows={2}
                                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={editedFares[i] ?? r.fare_raw ?? ''}
                                onChange={(e) => setEditedFares(prev => ({ ...prev, [i]: e.target.value }))}
                                placeholder={r.fare_raw}
                                className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-400">{r.sheet_name}</td>
                            <td className="px-3 py-2">
                              {r.path_linestring ? (
                                <button
                                  onClick={() => {
                                    const geojson = wktToGeoJson(r.path_linestring!, r.route_name_normalized);
                                    setPreviewGeoJson(geojson as FeatureCollection);
                                    setPreviewTitle(r.route_name_normalized);
                                  }}
                                  className="px-2 py-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded text-xs font-medium transition-colors cursor-pointer"
                                  title={`${countWktPoints(r.path_linestring)} points`}
                                >
                                  GeoJSON ({countWktPoints(r.path_linestring)} pts)
                                </button>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    <div className="px-3 py-2 bg-gray-50 text-xs text-gray-400 border-t border-gray-100">
                      {batchPreview.sample.route_templates.length} of {batchPreview.counts.route_templates} routes
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {batchPreview.warnings.length > 0 && (
                  <details className="text-amber-600 text-sm">
                    <summary className="font-medium cursor-pointer">{batchPreview.warnings.length} warnings</summary>
                    <ul className="mt-1 text-xs space-y-0.5 pl-4 list-disc">{batchPreview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                  </details>
                )}
                {batchPreview.errors.length > 0 && (
                  <div className="bg-red-50 rounded-md p-3 border border-red-200">
                    <p className="text-sm text-red-600 font-medium">{batchPreview.errors.length} errors</p>
                    <ul className="mt-1 text-xs text-red-700 space-y-0.5 pl-4 list-disc">{batchPreview.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <button onClick={async () => {
                    if (!batchPreview) return;
                    setBatchLoading(true);
                    try {
                      // Build routeEdits from state
                      const routeEdits: Record<number, { route_name?: string; schedule_raw?: string; fare_raw?: string }> = {};
                      batchPreview.sample.route_templates.forEach((_, i) => {
                        const edits: { route_name?: string; schedule_raw?: string; fare_raw?: string } = {};
                        if (renamedRoutes[i] !== undefined) edits.route_name = renamedRoutes[i];
                        if (editedSchedules[i] !== undefined) edits.schedule_raw = editedSchedules[i];
                        if (editedFares[i] !== undefined) edits.fare_raw = editedFares[i];
                        if (Object.keys(edits).length > 0) routeEdits[i] = edits;
                      });
                      const res = await commitImport({ importId: batchPreview.importId, mode: 'export_sql', conflictStrategy: 'upsert', routeEdits });
                      if (res.sql) setPendingSql({ sql: res.sql, title: 'Batch Import SQL' });
                    } catch (err: any) { setMessage({ type: 'error', text: err.message }); }
                    finally { setBatchLoading(false); }
                  }} disabled={batchLoading || batchPreview.errors.length > 0}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm disabled:opacity-50 cursor-pointer transition-colors">
                    {batchLoading ? 'Processing...' : 'Preview SQL'}
                  </button>
                  <button onClick={async () => {
                    if (!batchPreview) return;
                    if (!confirm('Apply all routes to database?')) return;
                    setBatchLoading(true);
                    try {
                      // Build routeEdits from state
                      const routeEdits: Record<number, { route_name?: string; schedule_raw?: string; fare_raw?: string }> = {};
                      batchPreview.sample.route_templates.forEach((_, i) => {
                        const edits: { route_name?: string; schedule_raw?: string; fare_raw?: string } = {};
                        if (renamedRoutes[i] !== undefined) edits.route_name = renamedRoutes[i];
                        if (editedSchedules[i] !== undefined) edits.schedule_raw = editedSchedules[i];
                        if (editedFares[i] !== undefined) edits.fare_raw = editedFares[i];
                        if (Object.keys(edits).length > 0) routeEdits[i] = edits;
                      });
                      const res = await commitImport({ importId: batchPreview.importId, mode: 'apply', conflictStrategy: 'upsert', routeEdits });
                      setBatchResult(res);
                      setMessage({ type: 'success', text: `Applied ${batchPreview.counts.route_templates} routes` });
                    } catch (err: any) { setMessage({ type: 'error', text: err.message }); }
                    finally { setBatchLoading(false); }
                  }} disabled={batchLoading || batchPreview.errors.length > 0}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm disabled:opacity-50 cursor-pointer transition-colors">
                    {batchLoading ? 'Processing...' : 'Apply to DB'}
                  </button>
                </div>
              </div>
            )}
            {batchResult && (
              <div className="bg-green-50 rounded-md p-3 border border-green-200">
                <p className="text-sm font-medium text-green-800">{batchResult.status === 'applied' ? 'Applied to DB' : 'SQL Exported'}</p>
                {batchResult.sql_artifact_url && <a href={batchResult.sql_artifact_url} download className="text-sm text-blue-600 hover:underline">Download SQL</a>}
              </div>
            )}
        </div>
      </div>}

      {/* SQL Action Modal */}
      {pendingSql && (
        <SqlActionModal
          sql={pendingSql.sql}
          title={pendingSql.title}
          onClose={() => setPendingSql(null)}
          onApplied={() => setMessage({ type: 'success', text: 'Applied to DB' })}
        />
      )}

      {/* Route Preview Modal with Map */}
      {previewGeoJson && (
        <RoutePreviewModal
          geojson={previewGeoJson}
          title={previewTitle}
          onClose={() => { setPreviewGeoJson(null); setPreviewTitle(''); }}
        />
      )}
    </div>
  );
}
